import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { formatDecimal, formatInteger, numberLocale } from '@/lib/language';
import { maskCustomerId } from '@/lib/maskCustomerId';
import { useChapterOpen } from '@/hooks/optimizationScore/chapterOpenPreference';
import type { LinkedAdsAccount } from '@/hooks/useLinkedAdsAccount';
import type { useAdsInterest } from '@/hooks/useAdsInterest';
import { formatMicros, hasEvidenceSplit, shareWithoutAds, SEGMENT_ORDER, type ParsedAdsFeedSummary, type SegmentKey } from '@/lib/adsFeedSummary';
import { AdsLeakList } from './AdsLeakList';

export interface AdsChapterBodyProps {
  parsed: ParsedAdsFeedSummary;
  linked: LinkedAdsAccount;
  state: 'ready' | 'stale' | 'no_data';
  feedId: string | null;
  interest: ReturnType<typeof useAdsInterest>;
  onOptimizeUnoptimized?: () => void;
  onShowDisapproved?: () => void;
  /** Siehe `AdsLeakListProps.busy`. */
  busy?: boolean;
}

function pct(part: number, whole: number): number {
  return whole > 0 ? Math.round((part / whole) * 100) : 0;
}

/**
 * Ein ISO-Datum (`2026-09-07`) als Tagesdatum der UI-Sprache.
 *
 * Hausform aus `pages/AdsPerformance.tsx:428` und `pages/PerformanceDashboard.tsx:192`,
 * inklusive `numberLocale`. Das angehaengte `T00:00:00` ist der Kern: ohne
 * Uhrzeit liest `new Date` eine reine Datumszeichenkette als UTC-Mitternacht,
 * und westlich von Greenwich zeigte die Fussnote dann den Vortag.
 */
function formatDay(iso: string, language: string | undefined): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString(numberLocale(language));
}

const SEGMENT_TONE: Record<SegmentKey, string> = {
  hero: 'text-foreground',
  mid: 'text-foreground',
  drain: 'text-destructive-ink',
  zombie: 'text-muted-foreground',
};

/**
 * Der offene Rumpf von Kapitel 3 (Spec 2, Punkte 2 bis 7, Artboard Main).
 * Alles kommt aus dem Cache-Eintrag; hier wird nichts nachgerechnet ausser
 * Anteilen fuer die Balken. Kein Ziel, kein Vergleich mit anderen Konten,
 * keine Summe ueber die Geldliste.
 */
export function AdsChapterBody({ parsed, linked, state, feedId, interest, onOptimizeUnoptimized, onShowDisapproved, busy }: AdsChapterBodyProps) {
  const { t, i18n } = useTranslation('optimization');
  const lang = i18n?.language;
  const s = parsed.summary;
  const currency = parsed.row.currency;
  const share = shareWithoutAds(s);
  const hero = s.segments.find((x) => x.key === 'hero');
  const heroShare = s.hero_revenue_share === null ? null : Math.round(s.hero_revenue_share * 100);
  const [leaksOpen, setLeaksOpen] = useChapterOpen(feedId, 'ads_leaks');
  const noData = state === 'no_data';

  return (
    <div className="flex flex-col gap-5">
      {/* Hauptzahl */}
      <div data-testid="ads-hero" className="flex flex-col gap-1.5">
        {noData ? (
          <p className="font-heading text-lg font-bold">{t('feedCheck.ads.hero.none', { total: formatInteger(s.products_total, lang) })}</p>
        ) : (
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="font-heading text-4xl font-bold leading-none tabular-nums text-destructive-ink">
              {formatInteger(s.products_without_ads, lang)}
            </span>
            <span className="font-heading text-lg font-bold">
              {/* `count` waehlt die Pluralform, `total` traegt die Anzeige:
                  i18next interpoliert `{{count}}` roh und schriebe "4940" ohne
                  Tausenderpunkt (Hausmuster aus dem Feed-Check-Kopf). */}
              {t('feedCheck.ads.hero.line', { count: s.products_without_ads, total: formatInteger(s.products_total, lang) })}
            </span>
          </div>
        )}
        <div className="flex items-center gap-3">
          <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-muted">
            <div className="h-full bg-destructive-ink" style={{ width: `${share}%` }} />
          </div>
          <span className="w-40 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
            {t('feedCheck.ads.hero.share', { share })}
          </span>
        </div>
        {!noData && (
          <p className="text-sm text-muted-foreground">
            {t('feedCheck.ads.hero.budget', { count: s.products_with_cost, withCost: formatInteger(s.products_with_cost, lang) })}{' '}
            {hero && hero.products > 0 && heroShare !== null && (
              <>{t('feedCheck.ads.hero.heroShare', { count: hero.products, hero: formatInteger(hero.products, lang), share: heroShare })}{' '}</>
            )}
            {t('feedCheck.ads.hero.reason')}
          </p>
        )}
      </div>

      {/* Drei Kacheln */}
      {/* WP5 A2O-07: drei Kacheln nebeneinander gaben am Handy je 63 px, "CHF 376" lief ueber. */}
      <div data-testid="ads-tiles" className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Tile
          label={t('feedCheck.ads.tiles.cost')}
          value={formatMicros(s.cost_micros, currency, lang)}
          sub={t('feedCheck.ads.tiles.costSub', { count: s.products_with_cost })}
        />
        <Tile
          label={t('feedCheck.ads.tiles.roas')}
          value={s.roas === null ? '--' : formatDecimal(s.roas, lang, 1)}
          // `roasSub` nennt CHF woertlich. Fuer jede andere Waehrung waere das
          // eine fremde Einheit an der eigenen Zahl, deshalb die generische
          // Fassung. Ohne Kosten gibt es gar keinen Hebel zu beschreiben.
          sub={s.roas === null
            ? t('feedCheck.ads.tiles.roasNone')
            : currency === 'CHF'
              ? t('feedCheck.ads.tiles.roasSub', { roas: formatDecimal(s.roas, lang, 1) })
              : t('feedCheck.ads.tiles.roasSubGeneric', { roas: formatDecimal(s.roas, lang, 1) })}
        />
        <Tile
          label={t('feedCheck.ads.tiles.revenue')}
          value={formatMicros(s.conversions_value * 1e6, currency, lang)}
          sub={t('feedCheck.ads.tiles.revenueSub', { count: Math.round(s.conversions) })}
        />
      </div>

      {/* Segmente */}
      <div data-testid="ads-segments" className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-sm font-heading font-bold">{t('feedCheck.ads.segments.title')}</h3>
          <div className="flex gap-3 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm bg-border-strong" />
              {t('feedCheck.ads.segments.legendCost')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm bg-primary" />
              {t('feedCheck.ads.segments.legendRevenue')}
            </span>
          </div>
        </div>
        {SEGMENT_ORDER.map((key) => {
          const seg = s.segments.find((x) => x.key === key);
          const products = seg?.products ?? 0;
          const costPct = pct(seg?.cost_micros ?? 0, s.cost_micros);
          const revPct = pct(seg?.conversions_value ?? 0, s.conversions_value);
          const meta = key === 'zombie'
            ? t('feedCheck.ads.segments.metaNoAds', { count: products })
            : t('feedCheck.ads.segments.meta', {
              count: products,
              roas: seg?.roas === null || seg?.roas === undefined ? '--' : formatDecimal(seg.roas, lang, 1),
            });
          return (
            <div key={key} data-testid={`ads-segment-${key}`} className="flex items-center gap-3 py-1.5">
              <div className="flex w-36 shrink-0 flex-col">
                {/* Nie der interne Segmentname (hero/mid/drain/zombie), immer
                    die Copy: "Verkaufen gut", "Kosten zu viel", ... */}
                <span className={`text-sm font-medium ${SEGMENT_TONE[key]}`}>{t(`feedCheck.ads.segments.${key}`)}</span>
                <span className="text-[11px] text-muted-foreground tabular-nums">{meta}</span>
              </div>
              <div className="flex flex-1 flex-col gap-0.5">
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-border-strong" style={{ width: `${costPct}%` }} />
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${revPct}%` }} />
                </div>
              </div>
              <div
                data-testid={`ads-shares-${key}`}
                className="w-24 shrink-0 text-right text-xs text-muted-foreground tabular-nums leading-tight"
              >
                {/* `segments.shares` traegt ein literales `<br/>`. Es wird hier
                    zerlegt und als zwei Knoten gerendert, NIE als HTML: die
                    Copy kommt aus einer JSON-Datei, und ein
                    `dangerouslySetInnerHTML` darauf waere ein Einfallstor,
                    das keine Zeile Uebersetzung wert ist. */}
                {t('feedCheck.ads.segments.shares', { cost: costPct, revenue: revPct })
                  .split('<br/>')
                  .map((line, i) => <div key={i}>{line}</div>)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Teaser */}
      <div data-testid="ads-teaser" className="flex items-center justify-between gap-5 rounded-md border border-border bg-background p-4">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-sm font-heading font-bold">{t('feedCheck.ads.teaser.title')}</span>
          <p className="text-[13px] leading-snug text-foreground/80">{t('feedCheck.ads.teaser.body')}</p>
        </div>
        {interest.reported ? (
          <span className="shrink-0 text-sm text-success-ink">{t('feedCheck.ads.teaser.reported')}</span>
        ) : (
          <Button
            data-testid="ads-interest-cta"
            size="sm"
            variant="outline"
            className="shrink-0 print:hidden"
            disabled={interest.isReporting}
            onClick={() => void interest.report()}
          >
            {t('feedCheck.ads.teaser.cta')}
          </Button>
        )}
      </div>

      {/* Geldliste, eingeklappt */}
      {!noData && (
        <AdsLeakList
          summary={s}
          currency={currency}
          open={leaksOpen}
          onToggle={() => setLeaksOpen(!leaksOpen)}
          onOptimizeUnoptimized={onOptimizeUnoptimized}
          onShowDisapproved={onShowDisapproved}
          busy={busy}
        />
      )}

      {/* Fussnote */}
      <p
        data-testid="ads-footnote"
        className={`text-[11px] leading-relaxed ${state === 'stale' ? 'text-warning-ink' : 'text-muted-foreground'}`}
      >
        {state === 'stale' && parsed.row.data_through && (
          <>{t('feedCheck.ads.footnote.stale', { date: formatDay(parsed.row.data_through, lang) })}{' '}</>
        )}
        {parsed.row.data_through
          ? (
            // v2 rendert `standV2` nur, wenn der Schreiber die Schwelle als
            // verlaesslich meldet (`evidence.reliable`, Review-Fund I2). Die
            // reine Existenz von `conversion_rate` reicht NICHT: bei CR 0
            // (Klicks, aber keine Conversions -- z. B. kaputtes Tracking) waere
            // "kaum Zufall" das Gegenteil der Wahrheit, und bei sehr kleiner CR
            // haelt die auf [20, 250] gekappte Schwelle das Versprechen
            // ebenfalls nicht. `conversion_rate !== null` bleibt zusaetzlich
            // stehen, damit TypeScript die Zahl unten als nicht-null erkennt --
            // `reliable=true` schliesst NULL im Schreiber immer aus.
            hasEvidenceSplit(s) && s.evidence.reliable && s.evidence.conversion_rate !== null
              ? t('feedCheck.ads.footnote.standV2', {
                date: formatDay(parsed.row.data_through, lang),
                id: maskCustomerId(linked.adsCustomerId),
                min: s.evidence.min_clicks,
                rate: formatDecimal(s.evidence.conversion_rate * 100, lang, 1),
              })
              : t('feedCheck.ads.footnote.stand', { date: formatDay(parsed.row.data_through, lang), id: maskCustomerId(linked.adsCustomerId) })
          )
          : t('feedCheck.ads.footnote.standNoData', { id: maskCustomerId(linked.adsCustomerId) })}
      </p>
    </div>
  );
}

function Tile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-md bg-muted/60 px-4 py-3.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="font-heading text-xl font-bold leading-tight tabular-nums break-words sm:text-2xl">{value}</span>
      <span className="text-xs text-muted-foreground">{sub}</span>
    </div>
  );
}
